import { RANGE, convertISOToPicker } from 'js/components/common/Fields/DatePicker';
import { confirm, length, regexp, required, range, money, number, email, func, exclusion } from 'js/utils/Validation/validationRules';
import {
    DATE_FIELD_DISPLAY_TYPE,
    FORM_FIELD_TYPE,
    FORM_RULE_CONDITION_OPERATOR,
    FORM_VALIDATION_RULE_TYPE
} from 'js/components/common/Form/constants';
import {
    TValidationMethodConfig,
    IValidationMethodConfigConfirm,
    IValidationMethodConfigEmail,
    IValidationMethodConfigLength,
    IValidationMethodConfigMoney,
    TValidationMethodConfigRange,
    IValidationMethodConfigRegexp,
    IValidationMethodConfigRequired,
    TValidationMethodConfigExclusion
} from 'js/utils/Validation/types';
import {
    TFormField,
    TFormFieldDropdown,
    TFormFieldValidationRule,
    TFormFieldValidationRuleDateRange,
    IFormRule
} from 'js/components/common/Form/types';
import { ICountryConfig } from 'js/components/system/App/store/types';

export const isValidDateString = (dateStr: string): boolean => {
    if (!dateStr) {
        return true;
    }

    if (dateStr.replace(/\D/g, '').length !== 8) {
        return false;
    }

    const date = new Date(dateStr);

    if (!date.getTime()) {
        return false;
    }

    return date.toISOString().includes(dateStr);
};

export const cmsDateStringToDate = (dateStr: string): Date | null => {
    if (dateStr === 'today') {
        return new Date();
    }

    const dateISORegexp = /^(\d{4})[-](0[1-9]|1[012])[-](0[1-9]|[12][0-9]|3[01])$/; // ISO format yyyy-mm-dd
    const isMatchedISOFormat = dateISORegexp.test(dateStr);

    if (isMatchedISOFormat) {
        return isValidDateString(dateStr) ? new Date(dateStr) : null;
    }

    const periodFormatRegexp = /^([+|-]{1}\d+)([dwmy])$/; // +/-[number]d/w/m/y
    const periodArr = dateStr.match(periodFormatRegexp);

    if (periodArr) {
        const factor = Number(periodArr[1]);
        const date = new Date();
        let y = date.getFullYear();
        let m = date.getMonth();
        let d = date.getDate();

        switch (periodArr[2]) {
            case 'm':
                m += factor;
                break;
            case 'd':
                d += factor;
                break;
            case 'w':
                d += 7 * factor;
                break;
            case 'y':
                y += factor;
                break;
            default:
                return null;
        }

        return new Date(y, m, d);
    }

    return null;
};

export const getCountriesList = (allowedCountries: ICountryConfig[] = []): { value: string; label: string }[] => allowedCountries
    .map(({ code, name }) => ({ value: code, label: name }));

export const getCurrenciesList = (allowedCurrencies: string[] = []): { value: string; label: string }[] => allowedCurrencies
    .map((currency) => ({ value: currency, label: currency }));

export const getPhoneAreaCodeList = (
    allowedCountries: ICountryConfig[] = []
): { value: string; label: string }[] => allowedCountries
    .map(({ code, phoneAreaCode }) => phoneAreaCode ? { value: code, label: phoneAreaCode } : null)
    .filter(Boolean) as { value: string; label: string }[];

export const imsToFieldValue = (
    fieldValue: string | number | boolean = '',
    field: TFormField,
    additionalData: { allowedCountries?: ICountryConfig[]; allowedCurrencies?: string[] } = {}
): string | boolean | number| null => {
    const { allowedCurrencies, allowedCountries } = additionalData;

    switch (field.type) {
        case FORM_FIELD_TYPE.CHECKBOX:
            if (field.imsValueChecked === fieldValue) {
                return true;
            } else if (field.imsValueUnchecked === fieldValue) {
                return false;
            }

            return null;
        case FORM_FIELD_TYPE.COUNTRY:
        case FORM_FIELD_TYPE.CURRENCY_CODE:
        case FORM_FIELD_TYPE.DROPDOWN: {
            const { selectedList, defaultValue, customList } = field as TFormFieldDropdown;

            const list = (() => {
                switch (selectedList) {
                    case 'countries': return getCountriesList(allowedCountries);
                    case 'currencies': return getCurrenciesList(allowedCurrencies);
                    default: return customList;
                }
            })();

            const possibleValues = list.map(({ value }) => value);

            return possibleValues.find(val => val === fieldValue)
                || possibleValues.find(val => val === defaultValue)
                || '';
        }
        case FORM_FIELD_TYPE.RADIO_BUTTON: {
            const { customList, defaultValue } = field;
            const possibleValues = customList.map(({ value }) => value);

            if (!possibleValues.includes(fieldValue as string)) {
                return defaultValue;
            }

            return fieldValue;
        }
        default: return fieldValue || '';
    }
};

export const imsToPhoneFieldValue = (
    fieldValue: string,
    additionalData: { allowedCountries: ICountryConfig[]; countrycode: string }
): [string, string] => {
    const { allowedCountries, countrycode } = additionalData;

    const countryByPhoneNumber = allowedCountries
        .filter(country => fieldValue.startsWith(country.phoneAreaCode))
        .sort((a, b) => b.phoneAreaCode.length - a.phoneAreaCode.length)?.[0];

    if (countryByPhoneNumber) {
        return [countryByPhoneNumber.code, fieldValue.slice(countryByPhoneNumber.phoneAreaCode.length)];
    }

    const countryByPhoneNumberWithoutPlus = allowedCountries
        .filter(country => fieldValue.startsWith(country.phoneAreaCode.replace('+', '')))
        .sort((a, b) => b.phoneAreaCode.length - a.phoneAreaCode.length)?.[0];

    if (countryByPhoneNumberWithoutPlus) {
        return [countryByPhoneNumberWithoutPlus.code, fieldValue.slice(countryByPhoneNumberWithoutPlus.phoneAreaCode.length - 1)];
    }

    return [countrycode, ''];
};

export const fieldToImsValue = (
    formData: Record<string, string | boolean>,
    field: TFormField,
    allowedCountries: ICountryConfig[]
): string | boolean | null => {
    const fieldValue = formData[field.name] || '';

    switch (field.type) {
        case FORM_FIELD_TYPE.CHECKBOX:
            return fieldValue ? field.imsValueChecked || true : field.imsValueUnchecked || false;
        case FORM_FIELD_TYPE.DATE_PICKER:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH_INNER: {
            const { year, month, day } = convertISOToPicker(fieldValue);
            const areSomePartNotSet = [year, month, day]
                .map(part => parseInt(part))
                .some(part => isNaN(part));
            return areSomePartNotSet ? null : fieldValue;
        }
        case FORM_FIELD_TYPE.PHONE:
        case FORM_FIELD_TYPE.CELLPHONE: {
            const phoneCode = allowedCountries.find(country => country.code === formData[`${field.name}-areaCode`])?.phoneAreaCode || '';
            const phone = formData[field.name];

            return phone ? `${phoneCode}${phone}` : '';
        }
        case FORM_FIELD_TYPE.CITY_OF_BIRTH:
            /**
             * cityOfBirth value consists of city's italian <official code> and <city name> separated by "-". Example: "A896-BIVONA".
             * <official code> is used for italianId validation
             * <city name> should be sent to IMS
             */
            return (fieldValue as string)
                .split('-')
                .slice(1)
                .join('-');
        default: return fieldValue;
    }
};

export const getExtraImsFields = <T>(extraImsFields = '', value: T): Record<string, T> => {
    return extraImsFields
        .split(',')
        .filter(Boolean)
        .reduce((accumulator, extraImsFieldName) => ({
            ...accumulator,
            [extraImsFieldName.trim()]: value
        }), {});
};

export const getRangeFromValidation = (field: TFormField): string => {
    const dateRangeRule = field.validation && field.validationRules?.find((validationRule) => validationRule.type === 'dateRange') as TFormFieldValidationRuleDateRange;

    if (!dateRangeRule) {
        return RANGE.PAST;
    }

    const minDate = cmsDateStringToDate(dateRangeRule.min);
    const maxDate = cmsDateStringToDate(dateRangeRule.max);

    if (!minDate || !maxDate) {
        return RANGE.PAST;
    }

    const minYear = minDate.getFullYear();
    const maxYear = maxDate.getFullYear();

    return `${minYear}-${maxYear}`;
};

export const validationRulesToValidationMethods = (
    validationRules: TFormFieldValidationRule[] = [],
    baseType?: FORM_FIELD_TYPE | null
): TValidationMethodConfig[] => {
    return validationRules
        .map((validation) => {
            switch (validation.type) {
                case FORM_VALIDATION_RULE_TYPE.required: {
                    if (baseType === FORM_FIELD_TYPE.CHECKBOX) {
                        return required({
                            ...validation,
                            error: validation.message,
                            predicate: (value: boolean) => value
                        }) as IValidationMethodConfigRequired;
                    }

                    if (baseType === FORM_FIELD_TYPE.DATE_PICKER) {
                        return required({
                            ...validation,
                            error: validation.message,
                            predicate: (value: string) => {
                                const [year = '', month = '', day = ''] = value?.split('-') || [];

                                return year && month && day;
                            }
                        }) as IValidationMethodConfigRequired;
                    }

                    return required({ ...validation, error: validation.message }) as IValidationMethodConfigRequired;
                }
                case FORM_VALIDATION_RULE_TYPE.length:
                    return length({ ...validation, error: validation.message }) as IValidationMethodConfigLength;
                case FORM_VALIDATION_RULE_TYPE.range:
                    return range({ ...validation, error: validation.message }) as TValidationMethodConfigRange;
                case FORM_VALIDATION_RULE_TYPE.dateRange:
                    return range({
                        ...validation,
                        error: validation.message,
                        errorMin: validation.dateRangeMinMessage,
                        errorMax: validation.dateRangeMaxMessage,
                        includeMin: true,
                        includeMax: true,
                        parse: (value: string) => cmsDateStringToDate(value)
                    }) as TValidationMethodConfigRange;
                case FORM_VALIDATION_RULE_TYPE.regexp: {
                    let re: RegExp | string = validation.regexp;

                    if (typeof re === 'string' && re.startsWith('/')) {
                        const flags = re.substring(re.lastIndexOf('/') + 1);

                        re = new RegExp(re.slice(1, re.length - flags.length - 1), flags);
                    }

                    return regexp({ ...validation, re, error: validation.message }) as IValidationMethodConfigRegexp;
                }
                case FORM_VALIDATION_RULE_TYPE.confirm:
                    return confirm({ ...validation, error: validation.message }) as IValidationMethodConfigConfirm;
                case FORM_VALIDATION_RULE_TYPE.amount:
                    return money({ ...validation, error: validation.message }) as IValidationMethodConfigMoney;
                case FORM_VALIDATION_RULE_TYPE.number:
                    return number({ ...validation, error: validation.message }) as IValidationMethodConfigRegexp;
                case FORM_VALIDATION_RULE_TYPE.email:
                    return email({ ...validation, error: validation.message }) as IValidationMethodConfigEmail;
                case FORM_VALIDATION_RULE_TYPE.internal:
                    return func({ ...validation }) as TValidationMethodConfig;
                case FORM_VALIDATION_RULE_TYPE.exclusion:
                    return exclusion({ ...validation, error: validation.message }) as TValidationMethodConfigExclusion;
                default:
                    return null;
            }
        })
        .filter(Boolean) as TValidationMethodConfig[];
};

export const getFieldValidations = (field: TFormField): Record<string, TValidationMethodConfig[]> => {
    const validations: Record<string, TValidationMethodConfig[]> = {};

    validations[field.name] = validationRulesToValidationMethods(field.validationRules, field.baseType);

    switch (field.type) {
        case FORM_FIELD_TYPE.TEXT:
        case FORM_FIELD_TYPE.EMAIL:
        case FORM_FIELD_TYPE.PASSWORD:
            if (field.requiresConfirmation) {
                validations[`${field.name}_confirmation`] = [
                    confirm({
                        field: field.name,
                        error: field.negativeTooltip
                    }) as IValidationMethodConfigConfirm
                ];
            }

            break;

        case FORM_FIELD_TYPE.DATE_PICKER:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH:
        case FORM_FIELD_TYPE.DATE_OF_BIRTH_INNER: {
            validations[field.name].push(
                func({
                    fn: (value: string) => {
                        const { year: yearStr, month: monthStr, day: dayStr } = convertISOToPicker(value);
                        const [year, month, day] = [yearStr, monthStr, dayStr].map(part => parseInt(part));
                        const areAllInputsEmpty = [year, month, day].every(part => isNaN(part));
                        const areAllInputsFilled = [year, month, day].every(part => !isNaN(part));
                        const errorMessage = field.type === FORM_FIELD_TYPE.DATE_PICKER
                            ? 'playtech.mobile.system.form.datePicker.internal.required'
                            : 'playtech.mobile.system.form.birthdate.internal.required';

                        if (field.displayType === DATE_FIELD_DISPLAY_TYPE.INPUT) {
                            return isValidDateString(value) ? null : [errorMessage];
                        }

                        if (areAllInputsEmpty || areAllInputsFilled) {
                            return null;
                        }

                        return [errorMessage];
                    }
                }) as TValidationMethodConfig<string>
            );

            break;
        }
        case FORM_FIELD_TYPE.BET_LIMITS:
        case FORM_FIELD_TYPE.LOSS_LIMITS:
        case FORM_FIELD_TYPE.DEPOSIT_LIMITS:
            delete validations[field.name];
            break;
        default:
            break;
    }

    return validations;
};

const addColumns = (fields: TFormField[]): TFormField[] => {
    let column = 1;

    return fields.map(field => {
        if (field.type === FORM_FIELD_TYPE.SEPARATOR) {
            column++;
            return null;
        }
        if ('innerFields' in field) {
            Object.keys(field.innerFields).forEach((key) => {
                (field.innerFields as Record<string, TFormField>)[key] = {
                    ...(field.innerFields as Record<string, TFormField>)[key],
                    column
                };
            });
        }
        return { ...field, column };
    }).filter(Boolean) as TFormField[];
};

export const getFormFields = (formFields: TFormField[]): TFormField[] => {
    const parseInnerFields = <T extends Record<string, TFormField>>(innerFields: T, parent: TFormField): T => {
        const entries = Object.entries(innerFields)
            .filter(([, innerField]) => innerField.active)
            .map(([name, innerField]) => [name, {
                ...innerField,
                id: `${parent.id}_${innerField.id}`,
                name: `${parent.name}-${innerField.name}`
            }]);

        return Object.fromEntries(entries) as T;
    };

    const actualFormFields = formFields
        .filter(field => field.active)
        .map(field => {
            return 'innerFields' in field
            ? {
                ...field,
                innerFields: parseInnerFields(field.innerFields, field)
            }
            : field;
        }) as TFormField[];

    const innerFields = actualFormFields
        .flatMap(field => 'innerFields' in field ? Object.values(field.innerFields) : []);
    const birthDateFields = actualFormFields
        .filter(field => field.type === FORM_FIELD_TYPE.DATE_OF_BIRTH)
        .map(field => field.name);
    const innerBirthDateFields = innerFields
        .filter(field => field.type === FORM_FIELD_TYPE.DATE_OF_BIRTH
            || field.type === FORM_FIELD_TYPE.DATE_OF_BIRTH_INNER
        )
        .map(field => field.name);

    return addColumns(actualFormFields.map((field) => {
        if (field.type !== FORM_FIELD_TYPE.PASSWORD) {
            return field;
        }

        const validationRules = field.validationRules?.map(rule => rule.type === FORM_VALIDATION_RULE_TYPE.exclusion
            ? { ...rule, dateFields: [...birthDateFields, ...innerBirthDateFields] }
            : rule
        );

        return {
            ...field,
            validationRules
        };
    }));
};

export const getFormRules = (formRules: IFormRule[], formFields: TFormField[]): IFormRule[] => {
    const actualFormFields = getFormFields(formFields);
    const formFieldsNames = actualFormFields.map(formField => formField.name);

    return formRules
        .map((rule) => ({
            ...rule,
            actions: rule.actions.filter(action => formFieldsNames.includes(action.field)),
            conditions: rule.conditions.filter(condition => formFieldsNames.includes(condition.field))
        }))
        .filter((rule) => rule.active && rule.actions.length && rule.conditions.length);
};

export const getHiddenByRulesFields = (
    { rules = [], values = {} }: { rules: IFormRule[]; values: Record<string, any> }
): string[] => {
    const fieldsVisibilityMap = rules.reduce<Record<string, boolean>>((acc, rule) => {
        // apply conditions by AND
        const areConditionsMet = rule.conditions.every(condition => {
            const { operator, field, value } = condition;

            switch (operator) {
                case FORM_RULE_CONDITION_OPERATOR.equal:
                    return values[field] === value;
                case FORM_RULE_CONDITION_OPERATOR.notEqual:
                    return values[field] !== value;
                default:
                    return false;
            }
        });

        rule.actions.forEach(action => {
            // apply rules by OR
            acc[action.field] = acc[action.field] || areConditionsMet;
        });

        return acc;
    }, {});

    rules.forEach(rule => {
        rule.conditions.forEach(condition => {
            if (condition.field in fieldsVisibilityMap && !fieldsVisibilityMap[condition.field]) {
                rule.actions.forEach(action => {
                    fieldsVisibilityMap[action.field] = false;
                });
            }
        });
    });

    return Object.keys(fieldsVisibilityMap)
        .filter(key => !fieldsVisibilityMap[key])
        .sort();
};

export const getLimitActions = (limitActions: string): string[] => limitActions
    .split(',')
    .map(action => action.trim())
    .filter(Boolean);

export const getStepColumns = (step: TFormField[]): Record<number, TFormField[]> => step.reduce((result, item) => {
    const key = item.column;

    if (!key) {
        return result;
    }

    if (!result[key]) {
        result[key] = [] as TFormField[];
    }

    result[key as keyof typeof result] = [...result[key], item];

    return result;
}, {} as Record<number, TFormField[]>);
